// Analytics Hook with GDPR-compliant loading for Facebook Pixel and Google Analytics
// Only initializes and tracks when user has accepted analytics cookies

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const COOKIE_CONSENT_KEY = "rentsense-cookie-consent";
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID || "";
const GA_MEASUREMENT_ID = "G-VKT0X96XSN";

let isFbInitialized = false;
let isGaInitialized = false;

/**
 * Check if user has consented to analytics cookies
 */
export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
};

/**
 * Initialize Google Analytics if consent is given
 */
export const initGoogleAnalytics = (): void => {
  if (isGaInitialized || !hasAnalyticsConsent() || !GA_MEASUREMENT_ID) return;

  // Load gtag.js script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);

  isGaInitialized = true;
};

/**
 * Initialize Facebook Pixel if consent is given
 */
export const initFacebookPixel = (): void => {
  if (isFbInitialized || !hasAnalyticsConsent() || !FB_PIXEL_ID) return;

  // Facebook Pixel base code
  const f = window;
  const b = document;
  const e = "script";
  const v = "https://connect.facebook.net/en_US/fbevents.js";

  if (f.fbq) return;

  const n: (...args: unknown[]) => void = function (...args) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (n as any).callMethod
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? (n as any).callMethod.apply(n, args)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      : (n as any).queue.push(args);
  };

  if (!f._fbq) f._fbq = n;
  f.fbq = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).push = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).loaded = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).version = "2.0";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).queue = [];

  const t = b.createElement(e) as HTMLScriptElement;
  t.async = true;
  t.src = v;

  const s = b.getElementsByTagName(e)[0];
  s?.parentNode?.insertBefore(t, s);

  f.fbq("init", FB_PIXEL_ID);
  isFbInitialized = true;

  // Track initial PageView
  f.fbq("track", "PageView");
};

/**
 * Initialize all analytics platforms
 */
export const initAnalytics = (): void => {
  initGoogleAnalytics();
  initFacebookPixel();
};

/**
 * Track a Google Analytics event
 */
export const trackGAEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (!hasAnalyticsConsent() || !GA_MEASUREMENT_ID) return;
  
  if (!isGaInitialized) {
    initGoogleAnalytics();
  }

  if (window.gtag) {
    window.gtag("event", eventName, params);
  }
};

/**
 * Track a Facebook Pixel event (only if consent is given)
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (!hasAnalyticsConsent()) return;
  
  // Initialize if not already done
  if (!isFbInitialized && FB_PIXEL_ID) {
    initFacebookPixel();
  }

  // Track on Facebook Pixel
  if (window.fbq && FB_PIXEL_ID) {
    if (params) {
      window.fbq("track", eventName, params);
    } else {
      window.fbq("track", eventName);
    }
  }

  // Also track on Google Analytics with mapped event names
  const gaEventMap: Record<string, string> = {
    PageView: "page_view",
    ViewContent: "view_item",
    Lead: "generate_lead",
    InitiateCheckout: "begin_checkout",
    Purchase: "purchase",
  };

  const gaEventName = gaEventMap[eventName] || eventName.toLowerCase();
  trackGAEvent(gaEventName, params);
};

// Convenience functions for common events
export const trackPageView = (): void => trackEvent("PageView");

export const trackViewContent = (contentName?: string): void =>
  trackEvent("ViewContent", contentName ? { content_name: contentName } : undefined);

export const trackLead = (contentName?: string): void =>
  trackEvent("Lead", contentName ? { content_name: contentName } : undefined);

export const trackInitiateCheckout = (value?: number, currency = "EUR"): void =>
  trackEvent("InitiateCheckout", value ? { value, currency } : undefined);

export const trackPurchase = (value: number, currency = "EUR"): void =>
  trackEvent("Purchase", { value, currency });
